// src/views/Restaurant/Configuracion/Integrations/components/Sii/Sii.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, Alert, Dropdown, Row, Col, Modal, Collapse } from 'react-bootstrap';
import { FaSave, FaTrash, FaFileInvoice, FaChevronDown, FaChevronUp, FaKey } from 'react-icons/fa';
import { toast } from 'react-toastify';
import LoadingSpinner from '../../../../../../components/common/LoadingSpinner';
import { clearSiiConfiguredCache } from '../../../../../../crud/management_api/useSiiConfigured';
import {
  getSimpleApiConfig,
  initializeSimpleApiConfig,
  updateSimpleApiField,
  deleteSimpleApiConfig,
  getCafInfo,
  SimpleApiConfig,
} from '../../../../../../crud/management_api/simple_api_config';
import './Sii.scss';

// Economic Activities Dictionary
const ACTIVIDADES_ECONOMICAS: Record<number, { nombre: string; afecto_iva: boolean; categoria: number; disponible_internet: boolean }> = {
  // ACTIVIDADES DE ALOJAMIENTO PARA ESTANCIAS CORTAS
  551001: { nombre: "ACTIVIDADES DE HOTELES", afecto_iva: true, categoria: 1, disponible_internet: true },
  551002: { nombre: "ACTIVIDADES DE MOTELES", afecto_iva: true, categoria: 1, disponible_internet: true },
  551003: { nombre: "ACTIVIDADES DE RESIDENCIALES PARA TURISTAS", afecto_iva: true, categoria: 1, disponible_internet: true },
  551009: { nombre: "OTRAS ACTIVIDADES DE ALOJAMIENTO PARA TURISTAS N.C.P.", afecto_iva: true, categoria: 1, disponible_internet: true },
  
  // ACTIVIDADES DE CAMPAMENTOS, PARQUES DE VEHÍCULOS DE RECREO Y PARQUES DE CARAVANAS
  552000: { nombre: "ACTIVIDADES DE CAMPING Y DE PARQUES PARA CASAS RODANTES", afecto_iva: true, categoria: 1, disponible_internet: true },
  
  // OTRAS ACTIVIDADES DE ALOJAMIENTO
  559001: { nombre: "ACTIVIDADES DE RESIDENCIALES PARA ESTUDIANTES Y TRABAJADORES", afecto_iva: true, categoria: 1, disponible_internet: true },
  559009: { nombre: "OTRAS ACTIVIDADES DE ALOJAMIENTO N.C.P.", afecto_iva: true, categoria: 1, disponible_internet: true },
  
  // ACTIVIDADES DE RESTAURANTES Y DE SERVICIO MÓVIL DE COMIDAS
  561000: { nombre: "ACTIVIDADES DE RESTAURANTES Y DE SERVICIO MÓVIL DE COMIDAS", afecto_iva: true, categoria: 1, disponible_internet: true },
  
  // SUMINISTRO DE COMIDAS POR ENCARGO Y OTRAS ACTIVIDADES DE SERVICIO DE COMIDAS
  562100: { nombre: "SUMINISTRO DE COMIDAS POR ENCARGO (SERVICIOS DE BANQUETERÍA)", afecto_iva: true, categoria: 1, disponible_internet: true },
  562900: { nombre: "SUMINISTRO INDUSTRIAL DE COMIDAS POR ENCARGO; CONCESIÓN DE SERVICIOS DE ALIMENTACIÓN", afecto_iva: true, categoria: 1, disponible_internet: true },
  
  // ACTIVIDADES DE SERVICIO DE BEBIDAS
  563001: { nombre: "ACTIVIDADES DE DISCOTECAS Y CABARET (NIGHT CLUB), CON PREDOMINIO DEL SERVICIO DE BEBIDAS", afecto_iva: true, categoria: 1, disponible_internet: true },
  563009: { nombre: "OTRAS ACTIVIDADES DE SERVICIO DE BEBIDAS N.C.P.", afecto_iva: true, categoria: 1, disponible_internet: true },



  //PANADERÍAS

  472105: { nombre: "VENTA AL POR MENOR EN COMERCIOS ESPECIALIZADOS DE PRODUCTOS DE PANADERÍA Y PASTELERÍA", afecto_iva: true, categoria: 1, disponible_internet: true }
};

const Sii: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasConfig, setHasConfig] = useState(false);
  const [config, setConfig] = useState<SimpleApiConfig>({
    Rut: '',
    SiiPassword: '',
    RazonSocial: '',
    Giro: '',
    ActividadEconomica: '',
    Resolucion: 0,
    FechaResolucion: '',
    DireccionOrigen: '',
    ComunaOrigen: '',
    CiudadOrigen: '',
    Telefono: '',
  });

  // Track original config to detect changes
  const [originalConfig, setOriginalConfig] = useState<SimpleApiConfig | null>(null);

  // Track which fields have been modified
  const [modifiedFields, setModifiedFields] = useState<Set<keyof SimpleApiConfig>>(new Set());

  // Collapsible card state
  const [isOpen, setIsOpen] = useState(true);

  // Password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [previousPassword, setPreviousPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Validation errors
  const [rutError, setRutError] = useState('');
  const [dateError, setDateError] = useState('');

  const [cafInfo, setCafInfo] = useState<{
    factura?: { current_folio: number; remaining: number };
    boleta?: { current_folio: number; remaining: number };
  }>({});

  const u = JSON.parse(localStorage.getItem('user') || '{}');
  const localId = u.local_id || 0;

  const loadCafInfo = useCallback(async () => {
    try {
      // Load CAF for Factura (tipo 33)
      const facturaResponse = await getCafInfo(localId, 33);
      if (facturaResponse.data) {
        setCafInfo(prev => ({
          ...prev,
          factura: {
            current_folio: facturaResponse.data.caf_data.current_folio,
            remaining: facturaResponse.data.remaining_folios,
          },
        }));
      }
    } catch (error) {
      console.log('No CAF info for Factura');
    }

    try {
      // Load CAF for Boleta (tipo 39)
      const boletaResponse = await getCafInfo(localId, 39);
      if (boletaResponse.data) {
        setCafInfo(prev => ({
          ...prev,
          boleta: {
            current_folio: boletaResponse.data.caf_data.current_folio,
            remaining: boletaResponse.data.remaining_folios,
          },
        }));
      }
    } catch (error) {
      console.log('No CAF info for Boleta');
    }
  }, [localId]);

  const loadConfiguration = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSimpleApiConfig(localId);
      
      // Check if config exists and is not an empty object
      const configData = response.data?.config;
      const isConfigValid = configData && 
                           typeof configData === 'object' && 
                           Object.keys(configData).length > 0;
      
      if (isConfigValid) {
        setConfig(configData);
        setOriginalConfig(configData); // Store original config for comparison
        setModifiedFields(new Set()); // Reset modified fields
        setHasConfig(true);
        
        // Load CAF info
        loadCafInfo();
      } else {
        setHasConfig(false);
        setOriginalConfig(null);
        setModifiedFields(new Set());
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        setHasConfig(false);
        setOriginalConfig(null);
        setModifiedFields(new Set());
      } else {
        console.error('Error loading SII configuration:', error);
        toast.error('Error al cargar la configuración del SII');
      }
    } finally {
      setLoading(false);
    }
  }, [localId, loadCafInfo]);

  // Load configuration on mount
  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  // Validation functions
  const validateRut = (rut: string): boolean => {
    // Format: XXXXXXX-X or XXXXXXXX-X
    const rutRegex = /^\d{7,8}-[\dkK]$/;
    return rutRegex.test(rut);
  };

  const validateDate = (date: string): boolean => {
    // Format: DD-MM-YYYY
    const dateRegex = /^\d{2}-\d{2}-\d{4}$/;
    if (!dateRegex.test(date)) return false;

    // Additional validation: check if date is valid
    const [day, month, year] = date.split('-').map(Number);
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < 1900 || year > 2100) return false;

    return true;
  };

  const handleInputChange = (field: keyof SimpleApiConfig, value: string | number) => {
    setConfig(prev => ({ ...prev, [field]: value }));

    // Validate RUT
    if (field === 'Rut' && typeof value === 'string') {
      if (value && !validateRut(value)) {
        setRutError('Formato inválido. Use XXXXXXX-X o XXXXXXXX-X');
      } else {
        setRutError('');
      }
    }

    // Validate date
    if (field === 'FechaResolucion' && typeof value === 'string') {
      if (value && !validateDate(value)) {
        setDateError('Formato inválido. Use DD-MM-YYYY');
      } else {
        setDateError('');
      }
    }
    
    // Track modified field only if it differs from original
    if (hasConfig && originalConfig) {
      if (originalConfig[field] !== value) {
        setModifiedFields(prev => new Set(prev).add(field));
      } else {
        // Remove from modified fields if value matches original
        setModifiedFields(prev => {
          const newSet = new Set(prev);
          newSet.delete(field);
          return newSet;
        });
      }
    }
  };

  const handlePasswordUpdate = async () => {
    // Validate that previous password matches
    if (previousPassword !== originalConfig?.SiiPassword) {
      setPasswordError('La contraseña anterior no coincide');
      setTimeout(() => {
        setPasswordError('');
      }, 3000);
      return;
    }

    // Update password
    try {
      await updateSimpleApiField({
        local_id: localId,
        field_name: 'SiiPassword',
        value: newPassword,
      });

      toast.success('Contraseña actualizada exitosamente');
      
      // Update configs
      setConfig(prev => ({ ...prev, SiiPassword: newPassword }));
      setOriginalConfig(prev => prev ? ({ ...prev, SiiPassword: newPassword }) : null);
      
      // Close modal and reset
      setShowPasswordModal(false);
      setPreviousPassword('');
      setNewPassword('');
      setPasswordError('');
    } catch (error) {
      console.error('Error updating password:', error);
      setPasswordError('Error al actualizar la contraseña');
      setTimeout(() => {
        setPasswordError('');
      }, 3000);
    }
  };

  const handleSaveConfiguration = async () => {
    // Validate required fields
    if (!config.Rut || !config.RazonSocial || !config.Giro) {
      toast.error('Por favor complete los campos obligatorios');
      return;
    }

    // Validate RUT format
    if (!validateRut(config.Rut)) {
      toast.error('Formato de RUT inválido. Use XXXXXXX-X o XXXXXXXX-X');
      return;
    }

    // Validate date format if provided
    if (config.FechaResolucion && !validateDate(config.FechaResolucion)) {
      toast.error('Formato de fecha inválido. Use DD-MM-YYYY');
      return;
    }

    setSaving(true);
    try {
      if (hasConfig) {
        // Update only modified fields
        if (modifiedFields.size === 0) {
          toast.info('No hay cambios para guardar');
          setSaving(false);
          return;
        }

        // Update each modified field individually
        const fieldsToUpdate = Array.from(modifiedFields);
        for (const field of fieldsToUpdate) {
          await updateSimpleApiField({
            local_id: localId,
            field_name: field,
            value: config[field],
          });
        }
        
        toast.success(`${fieldsToUpdate.length} campo(s) actualizado(s) exitosamente`);
        
        // Clear modified fields and update original config
        setModifiedFields(new Set());
        setOriginalConfig(config);
      } else {
        // Initialize configuration
        await initializeSimpleApiConfig({
          local_id: localId,
          ...config,
        });
        setHasConfig(true);
        setOriginalConfig(config);
        setModifiedFields(new Set());
        toast.success('Integración con SII inicializada exitosamente');
      }
      loadConfiguration();
      clearSiiConfiguredCache(localId);
    } catch (error) {
      console.error('Error saving configuration:', error);
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfiguration = async () => {
    if (!window.confirm('¿Está seguro de eliminar la integración con SII? Esta acción no se puede deshacer.')) {
      return;
    }

    setSaving(true);
    try {
      await deleteSimpleApiConfig(localId);
      setHasConfig(false);
      setConfig({
        Rut: '',
        SiiPassword: '',
        RazonSocial: '',
        Giro: '',
        ActividadEconomica: '',
        Resolucion: 0,
        FechaResolucion: '',
        DireccionOrigen: '',
        ComunaOrigen: '',
        CiudadOrigen: '',
        Telefono: '',
      });
      setOriginalConfig(null);
      setModifiedFields(new Set());
      setCafInfo({});
      clearSiiConfiguredCache(localId);
      toast.success('Integración eliminada exitosamente');
    } catch (error) {
      console.error('Error deleting configuration:', error);
      toast.error('Error al eliminar la integración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner text="Cargando configuración SII" transparent />;
  }

  const getActividadNombre = (codigo: string) => {
    const codigoNum = parseInt(codigo);
    return ACTIVIDADES_ECONOMICAS[codigoNum]?.nombre || 'Seleccione una actividad';
  };

  return (
    <div className="sii-integration-container">
      {/* Collapsible Card */}
      <div className="sii-card">
        <div 
          className="sii-card-header" 
          onClick={() => setIsOpen(!isOpen)}
          style={{ cursor: 'pointer' }}
        >
          <div className="sii-card-title">
            <FaFileInvoice className="title-icon" />
            <h3>Integración SII</h3>
          </div>
          <div className="sii-card-header-actions">
            {hasConfig && (
              <div className="sii-status-badge active">
                <div className="status-dot"></div>
                <span>Activo</span>
              </div>
            )}
            {isOpen ? <FaChevronUp /> : <FaChevronDown />}
          </div>
        </div>

        <Collapse in={isOpen}>
          <div className="sii-card-body">
            {!hasConfig && (
              <Alert variant="info" className="sii-alert">
                <FaFileInvoice /> Configure la integración con el SII para habilitar la emisión de documentos tributarios electrónicos (DTE).
              </Alert>
            )}

            {/* CAF Info */}
            {hasConfig && (cafInfo.factura || cafInfo.boleta) && (
              <div className="caf-info-section">
                <h4>Estado de Folios (CAF)</h4>
                <div className="caf-cards">
                  {cafInfo.factura && (
                    <div className="caf-card">
                      <div className="caf-card-header">
                        <FaFileInvoice />
                        <span>Facturas (Tipo 33)</span>
                      </div>
                      <div className="caf-card-body">
                        <div className="caf-stat">
                          <span className="label">Folio Actual:</span>
                          <span className="value">{cafInfo.factura.current_folio}</span>
                        </div>
                        <div className="caf-stat">
                          <span className="label">Folios Restantes:</span>
                          <span className={`value ${cafInfo.factura.remaining < 50 ? 'warning' : ''}`}>
                            {cafInfo.factura.remaining}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  {cafInfo.boleta && (
                    <div className="caf-card">
                      <div className="caf-card-header">
                        <FaFileInvoice />
                        <span>Boletas (Tipo 39)</span>
                      </div>
                      <div className="caf-card-body">
                        <div className="caf-stat">
                          <span className="label">Folio Actual:</span>
                          <span className="value">{cafInfo.boleta.current_folio}</span>
                        </div>
                        <div className="caf-stat">
                          <span className="label">Folios Restantes:</span>
                          <span className={`value ${cafInfo.boleta.remaining < 50 ? 'warning' : ''}`}>
                            {cafInfo.boleta.remaining}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Configuration Form */}
            <div className="sii-config-form">
              <h4>Datos del Emisor</h4>
              
              <Row className="g-3">
                <Col lg={6} md={12}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label required">RUT del Emisor</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="12345678-9"
                      value={config.Rut}
                      onChange={(e) => handleInputChange('Rut', e.target.value)}
                      className={`modern-input ${rutError ? 'is-invalid' : ''}`}
                    />
                    {rutError && <Form.Text className="text-danger">{rutError}</Form.Text>}
                  </Form.Group>
                </Col>

                <Col lg={6} md={12}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label required">Contraseña SII</Form.Label>
                    <div className="password-field-container">
                      <Form.Control
                        type="password"
                        placeholder="Contraseña del portal SII"
                        value={config.SiiPassword}
                        onChange={(e) => handleInputChange('SiiPassword', e.target.value)}
                        className="modern-input"
                        readOnly={hasConfig}
                        disabled={hasConfig}
                      />
                      {hasConfig && (
                        <Button
                          variant="none"
                          className="password-update-button"
                          onClick={() => setShowPasswordModal(true)}
                        >
                          <FaKey /> Actualizar
                        </Button>
                      )}
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              {/* Security Information Banner */}
              <Row className="g-3 mt-2">
                <Col xs={12}>
                  <div className="security-info-banner">
                    <div className="security-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                      </svg>
                    </div>
                    <div className="security-message">
                      <strong>Seguridad garantizada:</strong> Todos tus datos sensibles están encriptados y protegidos. Tu información está completamente segura.
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className="g-3 mt-2">
                <Col xs={12}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label required">Razón Social</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Nombre de la empresa"
                      value={config.RazonSocial}
                      onChange={(e) => handleInputChange('RazonSocial', e.target.value)}
                      className="modern-input"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 mt-2">
                <Col xs={12}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label required">Giro</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Actividad comercial principal"
                      value={config.Giro}
                      onChange={(e) => handleInputChange('Giro', e.target.value)}
                      className="modern-input"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 mt-2">
                <Col xs={12}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label required">Actividad Económica</Form.Label>
                    <Dropdown className="actividad-dropdown">
                      <Dropdown.Toggle 
                        variant="none" 
                        className="modern-input dropdown-toggle-custom"
                        id="actividad-economica-dropdown"
                      >
                        {getActividadNombre(config.ActividadEconomica)}
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="actividad-dropdown-menu">
                        {Object.entries(ACTIVIDADES_ECONOMICAS).map(([codigo, data]) => (
                          <Dropdown.Item
                            key={codigo}
                            onClick={() => handleInputChange('ActividadEconomica', codigo)}
                            active={config.ActividadEconomica === codigo}
                          >
                            <div className="actividad-item">
                              <span className="actividad-codigo">{codigo}</span>
                              <span className="actividad-nombre">{data.nombre}</span>
                            </div>
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 mt-2">
                <Col md={6}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label required">N° Resolución SII</Form.Label>
                    <Form.Control
                      type="number"
                      placeholder="0 para certificación"
                      value={config.Resolucion}
                      onChange={(e) => handleInputChange('Resolucion', parseInt(e.target.value) || 0)}
                      className="modern-input"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label required">Fecha Resolución</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="DD-MM-YYYY"
                      value={config.FechaResolucion}
                      onChange={(e) => handleInputChange('FechaResolucion', e.target.value)}
                      className={`modern-input ${dateError ? 'is-invalid' : ''}`}
                    />
                    {dateError && <Form.Text className="text-danger">{dateError}</Form.Text>}
                  </Form.Group>
                </Col>
              </Row>

              <h4 className="section-title">Datos de Ubicación</h4>

              <Row className="g-3">
                <Col xs={12}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label required">Dirección</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Dirección de la empresa"
                      value={config.DireccionOrigen}
                      onChange={(e) => handleInputChange('DireccionOrigen', e.target.value)}
                      className="modern-input"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 mt-2">
                <Col md={6}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label required">Comuna</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Comuna"
                      value={config.ComunaOrigen}
                      onChange={(e) => handleInputChange('ComunaOrigen', e.target.value)}
                      className="modern-input"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label required">Ciudad</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Ciudad"
                      value={config.CiudadOrigen}
                      onChange={(e) => handleInputChange('CiudadOrigen', e.target.value)}
                      className="modern-input"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3 mt-2">
                <Col xs={12}>
                  <Form.Group className="form-field">
                    <Form.Label className="field-label required">Teléfono</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="+56 9 1234 5678"
                      value={config.Telefono}
                      onChange={(e) => handleInputChange('Telefono', e.target.value)}
                      className="modern-input"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Action Buttons */}
              <div className="sii-actions">
                {hasConfig && (
                  <Button
                    variant="none"
                    className="form-button-cancel"
                    onClick={handleDeleteConfiguration}
                    disabled={saving}
                  >
                    <FaTrash /> Eliminar
                  </Button>
                )}
                <Button
                  variant="none"
                  className="form-button-create"
                  onClick={handleSaveConfiguration}
                  disabled={saving}
                >
                  <FaSave /> {hasConfig ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </div>
          </div>
        </Collapse>
      </div>

      {/* Password Update Modal */}
      <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered>
        <Modal.Header className="modal-header">
          <div className="form-modal-title-container">
            <h2 className="form-modal-title">
              Actualizar Contraseña SII
            </h2>
          </div>
          <button
            type="button"
            className="modal-close-button"
            onClick={() => {
              setShowPasswordModal(false);
              setPreviousPassword('');
              setNewPassword('');
              setPasswordError('');
            }}
          >
            ×
          </button>
        </Modal.Header>
        <Modal.Body style={{ padding: '1.5rem' }}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Contraseña Anterior</Form.Label>
              <Form.Control
                type="password"
                placeholder="Ingrese contraseña anterior"
                value={previousPassword}
                onChange={(e) => setPreviousPassword(e.target.value)}
                isInvalid={!!passwordError}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Nueva Contraseña</Form.Label>
              <Form.Control
                type="password"
                placeholder="Ingrese nueva contraseña"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </Form.Group>
            {passwordError && (
              <Alert variant="danger" className="mb-0">
                {passwordError}
              </Alert>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer className="modal-footer">
          <div className="form-modal-footer-custom">
            <Button 
              variant="none"
              className="btn form-button-cancel"
              onClick={() => {
                setShowPasswordModal(false);
                setPreviousPassword('');
                setNewPassword('');
                setPasswordError('');
              }}
            >
              Cancelar
            </Button>
            <Button 
              variant="none"
              className="btn form-button-create"
              onClick={handlePasswordUpdate}
              disabled={!previousPassword || !newPassword}
            >
              Actualizar Contraseña
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Sii;